/**
 * LandingPage.tsx
 * Public marketing landing page at /#/
 * Hero → Problem → Features → Tiers → CTA
 */
import { useNavigate } from 'react-router-dom'

const NAV: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
  background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(12px)',
  borderBottom: '1px solid rgba(201,168,76,0.15)',
  display: 'flex', alignItems: 'center', padding: '0 32px', height: 60,
  fontFamily: "'Segoe UI', system-ui, sans-serif",
}

const BTN = (extra?: React.CSSProperties): React.CSSProperties => ({
  padding: '10px 24px', borderRadius: 8, fontWeight: 700,
  fontSize: 14, cursor: 'pointer', border: 'none', ...extra,
})

const GOLD = '#c9a84c'
const NAVY = '#0a1628'
const CARD: React.CSSProperties = {
  background: '#0f1a2e', border: '1px solid rgba(201,168,76,0.15)',
  borderRadius: 16, padding: '28px 24px',
}

const features = [
  { icon: '📋', title: 'Shift Scheduling', desc: 'Post shifts, assign officers to posts, manage break rotations — all from one screen.' },
  { icon: '📻', title: 'Equipment Tracking', desc: 'Radios, keys, recorders, and access badges tracked in real time across your entire team.' },
  { icon: '📱', title: 'Mobile Command', desc: 'Officers check in from their phones. Supervisors see everything from a command dashboard.' },
  { icon: '☁️', title: 'Cloud Sync', desc: 'Every change pushes instantly to all connected devices. No refresh needed.' },
  { icon: '🔒', title: 'Role-Based Access', desc: 'Officers, supervisors, and directors each see exactly what they need — nothing more.' },
  { icon: '📊', title: 'Audit Trail', desc: 'Every login, approval, and change is logged. Stay compliant and accountable.' },
]

const tiers = [
  {
    name: 'Shift', price: '$49.99', desc: 'For small teams getting started.',
    features: ['Shift scheduling', 'Equipment tracking', 'Daily posting', 'Cloud sync', 'Mobile app', 'Audit log'],
    cta: 'Start free trial', highlight: false, available: true,
  },
  {
    name: 'Terrain+NFC', price: '$149.99', desc: 'For teams that need location awareness.',
    features: ['Everything in Shift', 'Terrain zone map', 'NFC checkpoints', 'Officer geolocation', 'Incident reports', 'Priority support'],
    cta: 'Coming soon', highlight: true, available: false,
  },
  {
    name: 'Command', price: '$399.99', desc: 'For enterprise security operations.',
    features: ['Everything in Terrain+NFC', 'Command dashboard', 'Multi-site management', 'REST API access', 'Camera integration', 'SLA guarantee'],
    cta: 'Coming soon', highlight: false, available: false,
  },
]

const testimonials = [
  { quote: 'Posting used to take 30 minutes at the start of every shift. Now it takes 3.', name: 'Director of Security', org: 'Fair Oaks Mall' },
  { quote: 'We finally know where every radio is at any given moment. No more lost equipment.', name: 'Security Supervisor', org: 'Regional Mall Group' },
]

export default function LandingPage() {
  const navigate = useNavigate()

  return (
    <div style={{ background: NAVY, color: '#e2e8f0', fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: '100vh' }}>

      {/* Nav */}
      <nav style={NAV}>
        <div style={{ fontSize: 20, fontWeight: 800, color: GOLD, letterSpacing: 2, flex: 1 }}>CASTELLAN</div>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          <span onClick={() => navigate('/pricing')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Pricing</span>
          <span onClick={() => navigate('/features')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Features</span>
          <span onClick={() => navigate('/docs')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Docs</span>
          <span onClick={() => navigate('/download')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Download</span>
          <span onClick={() => navigate('/find-agency')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Sign in</span>
          <button onClick={() => navigate('/signup')} style={BTN({ background: GOLD, color: NAVY })}>Get started</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ paddingTop: 140, paddingBottom: 100, textAlign: 'center', maxWidth: 760, margin: '0 auto', padding: '140px 24px 100px' }}>
        <div style={{ display: 'inline-block', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 20, padding: '6px 16px', fontSize: 12, fontWeight: 700, color: GOLD, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 24 }}>
          Security Management Platform
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 6vw, 64px)', fontWeight: 800, lineHeight: 1.1, margin: '0 0 24px', color: '#f1f5f9' }}>
          Run your security operation<br />
          <span style={{ color: GOLD }}>like a command center.</span>
        </h1>
        <p style={{ fontSize: 18, color: '#94a3b8', lineHeight: 1.7, maxWidth: 560, margin: '0 auto 40px' }}>
          Castellan replaces the clipboards, walkie-talkie guesswork, and post-shift chaos with a real-time platform your whole team can use — from phones, tablets, and desktops.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/signup')} style={BTN({ background: GOLD, color: NAVY, padding: '14px 32px', fontSize: 16 })}>
            Start your free 14-day trial →
          </button>
          <button onClick={() => navigate('/pricing')} style={BTN({ background: 'transparent', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', padding: '14px 32px', fontSize: 16 })}>
            View pricing
          </button>
        </div>
        <p style={{ color: '#475569', fontSize: 13, marginTop: 16 }}>No credit card required on Shift tier · Cancel anytime</p>
      </section>

      {/* Stats */}
      <section style={{ borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '40px 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 32, textAlign: 'center' }}>
          {[['Real-time', 'Cloud sync across all devices'], ['3 tiers', 'From small teams to enterprise'], ['Android + Windows', 'Native apps available'], ['14-day', 'Free trial on Shift tier']].map(([stat, label]) => (
            <div key={stat}>
              <div style={{ fontSize: 28, fontWeight: 800, color: GOLD }}>{stat}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Problem */}
      <section style={{ padding: '80px 24px', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 36, fontWeight: 800, color: '#f1f5f9', marginBottom: 20 }}>Built for the way security actually works.</h2>
        <p style={{ fontSize: 16, color: '#94a3b8', lineHeight: 1.8 }}>
          Most security operations still run on printed posting sheets, group texts, and radio chatter. Equipment goes missing. Shifts get chaotic. Post-shift paperwork takes an hour. Castellan replaces all of it — one platform, every shift, every officer.
        </p>
      </section>

      {/* Features */}
      <section style={{ padding: '0 24px 80px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          {features.map(f => (
            <div key={f.title} style={CARD}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>{f.icon}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>{f.title}</div>
              <div style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.6 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing preview */}
      <section style={{ background: '#060d18', padding: '80px 24px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ fontSize: 36, fontWeight: 800, color: '#f1f5f9', textAlign: 'center', marginBottom: 12 }}>Simple, transparent pricing.</h2>
          <p style={{ textAlign: 'center', color: '#64748b', marginBottom: 48, fontSize: 16 }}>Start free. Scale when you're ready.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {tiers.map(t => (
              <div key={t.name} style={{
                ...CARD, position: 'relative',
                borderColor: t.highlight ? GOLD : 'rgba(201,168,76,0.15)',
                borderWidth: t.highlight ? 2 : 1,
                opacity: t.available ? 1 : 0.7,
              }}>
                {t.highlight && (
                  <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: GOLD, color: NAVY, fontSize: 11, fontWeight: 700, padding: '3px 14px', borderRadius: 20, whiteSpace: 'nowrap' }}>MOST POPULAR</div>
                )}
                <div style={{ color: GOLD, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{t.name}</div>
                <div style={{ fontSize: 36, fontWeight: 800, color: '#f1f5f9', marginBottom: 4 }}>{t.price}<span style={{ fontSize: 14, color: '#64748b', fontWeight: 400 }}>/mo</span></div>
                <div style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>{t.desc}</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
                  {t.features.map(f => <li key={f} style={{ fontSize: 14, color: '#94a3b8', padding: '4px 0' }}><span style={{ color: '#4ade80', marginRight: 8 }}>✓</span>{f}</li>)}
                </ul>
                <button
                  onClick={() => t.available && navigate('/signup')}
                  style={BTN({ width: '100%', background: t.available ? (t.highlight ? GOLD : 'rgba(201,168,76,0.15)') : '#1e293b', color: t.available ? (t.highlight ? NAVY : GOLD) : '#475569', cursor: t.available ? 'pointer' : 'not-allowed' })}
                >{t.cta}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: '80px 24px', maxWidth: 900, margin: '0 auto' }}>
        <h2 style={{ fontSize: 36, fontWeight: 800, color: '#f1f5f9', textAlign: 'center', marginBottom: 48 }}>Trusted by security professionals.</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
          {testimonials.map(t => (
            <div key={t.name} style={{ ...CARD }}>
              <div style={{ fontSize: 32, color: GOLD, marginBottom: 12 }}>"</div>
              <p style={{ fontSize: 16, color: '#cbd5e1', lineHeight: 1.7, marginBottom: 16 }}>{t.quote}</p>
              <div style={{ fontSize: 13, color: '#64748b' }}>{t.name} · {t.org}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: '#060d18', borderTop: '1px solid rgba(201,168,76,0.15)', padding: '80px 24px', textAlign: 'center' }}>
        <h2 style={{ fontSize: 36, fontWeight: 800, color: '#f1f5f9', marginBottom: 16 }}>Ready to run a tighter operation?</h2>
        <p style={{ color: '#64748b', fontSize: 16, marginBottom: 32 }}>14 days free. No credit card on Shift tier. Cancel anytime.</p>
        <button onClick={() => navigate('/signup')} style={BTN({ background: GOLD, color: NAVY, padding: '16px 40px', fontSize: 16 })}>
          Get started free →
        </button>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '32px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: GOLD, letterSpacing: 2 }}>CASTELLAN</div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {[['Pricing', '/pricing'], ['Features', '/features'], ['Docs', '/docs'], ['Download', '/download'], ['Privacy', '/privacy'], ['Terms', '/terms'], ['Sign in', '/find-agency']].map(([l, h]) => (
            <span key={l} onClick={() => navigate(h)} style={{ color: '#475569', cursor: 'pointer', fontSize: 13 }}>{l}</span>
          ))}
        </div>
        <div style={{ color: '#334155', fontSize: 12 }}>© 2026 Castellan Security. All rights reserved.</div>
      </footer>
    </div>
  )
}
